import React, { useState, useEffect, useRef } from 'react';
import { Radio, Music, Calendar, Mail, Phone, MapPin, Send, Heart, Facebook, Instagram, Twitter, Play, Volume2, VolumeX, Cloud, Sun, CloudRain, Menu, X } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { useToast } from '../hooks/use-toast';

const Home3 = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [feedbackForm, setFeedbackForm] = useState({ name: '', email: '', message: '' });
  const [promoVideoMuted, setPromoVideoMuted] = useState(true);
  const [audioMuted, setAudioMuted] = useState(true);
  const [featuredShows, setFeaturedShows] = useState([
    { show: 'Reliving Single Ep 5', time: '1:00 PM', status: 'On Air', desc: 'Podcast' },
    { show: 'DJ MG West Coast', time: '3:00 PM', status: 'Up Next', desc: 'West Coast Mix' },
    { show: 'Quiet Storm', time: '11:00 PM', status: 'Later', desc: 'Slow Jams' },
  ]);
  const [gratitudeMessages, setGratitudeMessages] = useState([
    { message: "Grateful for the music that connects us to our roots", name: "Maria L." },
    { message: "Thank you for bringing back the classics! Makes my day every time", name: "James K." },
    { message: "Love the morning devotions - starting my day with purpose", name: "Patricia W." },
    { message: "The history shows are so educational and inspiring", name: "David M." },
  ]);
  const [gratitudeForm, setGratitudeForm] = useState({ message: '', name: '' });
  const [attributionCredits, setAttributionCredits] = useState([
    { platform: "IG", handle: "@wvodpower93" },
    { platform: "IG", handle: "@infamoussoljahsound" },
    { platform: "TikTok", handle: "@wvodpower93" },
    { platform: "TikTok", handle: "@djmg_official" },
    { platform: "YouTube", handle: "WVOD Power 93" },
    { platform: "YouTube", handle: "Infamous Soljah Sound" },
  ]);
  const promoVideoRef = useRef(null);
  const audioRef = useRef(null);
  const { toast } = useToast();
  
  const API_URL = process.env.REACT_APP_BACKEND_URL || '';

  // Fetch gratitude messages
  useEffect(() => {
    const fetchGratitude = async () => {
      try {
        const response = await fetch(`${API_URL}/api/gratitude`);
        const data = await response.json();
        if (data && data.length > 0) {
          setGratitudeMessages(data);
        }
      } catch (error) {
        console.log('Using default gratitude messages');
      }
    };
    fetchGratitude();
  }, []);

  // Fetch attribution credits
  useEffect(() => {
    const fetchAttribution = async () => {
      try {
        await fetch(`${API_URL}/api/attribution/init`, { method: 'POST' });
        const response = await fetch(`${API_URL}/api/attribution`);
        const data = await response.json();
        if (data && data.length > 0) {
          setAttributionCredits(data);
        }
      } catch (error) {
        console.log('Using default attribution credits');
      }
    };
    fetchAttribution();
  }, []);

  // Submit gratitude message
  const handleGratitudeSubmit = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch(`${API_URL}/api/gratitude`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message: gratitudeForm.message,
          name: gratitudeForm.name || 'Anonymous'
        })
      });
      
      if (response.ok) {
        const newMessage = await response.json();
        setGratitudeMessages([newMessage, ...gratitudeMessages]);
        setGratitudeForm({ message: '', name: '' });
        toast({
          title: "Gratitude Shared!",
          description: "Your message has been added to our gratitude board.",
        });
      }
    } catch (error) {
      toast({
        title: "Thank you!",
        description: "Your gratitude has been received.",
      });
    }
  };

  // Submit contact form
  const handleContactSubmit = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch(`${API_URL}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(feedbackForm)
      });
      
      if (response.ok) {
        setFeedbackForm({ name: '', email: '', message: '' });
        toast({
          title: "Message Sent!",
          description: "Thank you for reaching out. We'll get back to you soon.",
        });
      }
    } catch (error) {
      toast({
        title: "Message Received!",
        description: "Thank you for your feedback.",
      });
    }
  };

  // Fetch featured shows from backend
  useEffect(() => {
    const fetchShows = async () => {
      try {
        // Initialize shows if needed
        await fetch(`${API_URL}/api/shows/init`, { method: 'POST' });
        // Fetch shows
        const response = await fetch(`${API_URL}/api/shows`);
        const data = await response.json();
        if (data && data.length > 0) {
          setFeaturedShows(data);
        }
      } catch (error) {
        console.log('Using default shows');
      }
    };
    fetchShows();
  }, []);

  // Toggle promo video mute
  const togglePromoMute = () => {
    if (promoVideoRef.current) {
      promoVideoRef.current.muted = !promoVideoRef.current.muted;
      setPromoVideoMuted(promoVideoRef.current.muted);
    }
  };

  // Toggle audio mute and play
  const toggleAudioMute = () => {
    if (audioRef.current) {
      if (audioMuted) {
        audioRef.current.muted = false;
        audioRef.current.play();
      } else {
        audioRef.current.muted = true;
        audioRef.current.pause();
      }
      setAudioMuted(!audioMuted);
    }
  };

  // Load Elfsight script for Instagram feed
  useEffect(() => {
    const script = document.createElement('script');
    script.src = 'https://static.elfsight.com/platform/platform.js';
    script.async = true;
    document.body.appendChild(script);
    
    return () => {
      // Cleanup if needed
      const existingScript = document.querySelector('script[src="https://static.elfsight.com/platform/platform.js"]');
      if (existingScript) {
        existingScript.remove();
      }
    };
  }, []);

  // Now Playing Track Component
  const NowPlayingTrack = () => {
    const [track, setTrack] = useState({ title: 'Loading...', artist: '' });
    
    useEffect(() => {
      const fetchTrack = async () => {
        try {
          const response = await fetch('https://public.radio.co/stations/s5f6084ed4/status');
          const data = await response.json();
          if (data.current_track && data.current_track.title) {
            // Parse the track title (often in "Artist - Song" format)
            const title = data.current_track.title;
            setTrack({ title: title, artist: '' });
          }
        } catch (error) {
          setTrack({ title: 'WVOD Power 93', artist: 'The Voice of the Diaspora' });
        }
      };
      
      fetchTrack();
      // Refresh every 30 seconds
      const interval = setInterval(fetchTrack, 30000);
      return () => clearInterval(interval);
    }, []);

    return (
      <div className="text-center mt-4">
        <div className="flex items-center justify-center gap-3 mb-2">
          <Music className="w-6 h-6 text-purple-400 animate-bounce-slow" />
          <p className="text-lg font-bold text-white truncate max-w-xs">{track.title}</p>
        </div>
        {track.artist && <p className="text-sm text-gray-400">{track.artist}</p>}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-900 via-black to-gray-900 text-white">
      {/* Modern Glassmorphic Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-black/40 backdrop-blur-xl border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 lg:px-8">
          <div className="flex items-center justify-between py-4">
            <div className="flex items-center gap-3 animate-fade-in">
              <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl flex items-center justify-center shadow-lg shadow-purple-500/50">
                <Radio className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-xl font-bold">WVOD POWER 93</h1>
                <p className="text-xs text-purple-400 uppercase tracking-widest">Voice of the Diaspora</p>
              </div>
            </div>
            
            {/* Desktop Nav */}
            <nav className="hidden md:flex items-center gap-6">
              <a href="#home" className="text-sm hover:text-purple-400 transition-colors duration-300">Home</a>
              <a href="#about" className="text-sm hover:text-purple-400 transition-colors duration-300">About</a>
              <a href="#schedule" className="text-sm hover:text-purple-400 transition-colors duration-300">Schedule</a>
              <a href="#events" className="text-sm hover:text-purple-400 transition-colors duration-300">Events</a>
              <a href="#contact" className="text-sm hover:text-purple-400 transition-colors duration-300">Contact</a>
              <button className="px-6 py-2 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full text-sm font-bold hover:shadow-lg hover:shadow-purple-500/50 transition-all duration-300 transform hover:scale-105">
                Listen Live
              </button>
            </nav>

            {/* Mobile Menu Button */}
            <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="md:hidden">
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>

          {/* Mobile Nav */}
          {mobileMenuOpen && (
            <div className="md:hidden pb-4 space-y-2 animate-slide-down">
              <a href="#home" className="block py-2 hover:text-purple-400 transition-colors" onClick={() => setMobileMenuOpen(false)}>Home</a>
              <a href="#about" className="block py-2 hover:text-purple-400 transition-colors" onClick={() => setMobileMenuOpen(false)}>About</a>
              <a href="#schedule" className="block py-2 hover:text-purple-400 transition-colors" onClick={() => setMobileMenuOpen(false)}>Schedule</a>
              <a href="#events" className="block py-2 hover:text-purple-400 transition-colors" onClick={() => setMobileMenuOpen(false)}>Events</a>
              <a href="#contact" className="block py-2 hover:text-purple-400 transition-colors" onClick={() => setMobileMenuOpen(false)}>Contact</a>
            </div>
          )}
        </div>
      </header>

      {/* Hero Section with Modern Layout */}
      <section id="home" className="min-h-screen flex items-center justify-center pt-20 px-4 relative overflow-hidden">
        {/* Animated Background */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-purple-500/20 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-pink-500/20 rounded-full blur-3xl animate-pulse delay-1000"></div>
        </div>

        <div className="relative z-10 max-w-5xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-purple-500/20 backdrop-blur-sm border border-purple-500/30 rounded-full mb-8 animate-bounce-slow">
            <span className="w-2 h-2 bg-purple-500 rounded-full animate-pulse"></span>
            <span className="text-sm font-mono uppercase tracking-widest text-purple-400">Live Now</span>
          </div>
          
          <h2 className="text-5xl md:text-7xl lg:text-8xl font-black mb-6 leading-tight animate-fade-in-up">
            Welcome to<br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-400 via-pink-400 to-purple-400 animate-gradient">
              WVOD Power 93
            </span>
          </h2>
          
          <p className="text-xl md:text-2xl text-gray-300 max-w-3xl mx-auto mb-12 leading-relaxed animate-fade-in-up delay-200">
            The ultimate destination for music the way it used to be. We bring you the best of classic hits, soulful melodies, and timeless tunes that resonate with the diaspora.
          </p>

          {/* Enhanced Aesthetic Radio Player */}
          <div className="max-w-3xl mx-auto mb-8 animate-fade-in-up delay-400">
            <div className="relative">
              {/* Glowing Background Effect */}
              <div className="absolute -inset-2 bg-gradient-to-r from-purple-500 via-pink-500 to-purple-500 rounded-3xl blur-xl opacity-50 animate-pulse"></div>
              
              {/* Player Container */}
              <Card className="relative bg-gradient-to-br from-white/10 via-white/5 to-white/10 backdrop-blur-2xl border-2 border-white/20 rounded-3xl overflow-hidden shadow-2xl">
                {/* Decorative Top Bar */}
                <div className="h-1 bg-gradient-to-r from-purple-500 via-pink-500 to-purple-500"></div>
                
                {/* Player Header */}
                <div className="p-6 bg-gradient-to-r from-purple-500/20 via-pink-500/20 to-purple-500/20 backdrop-blur-sm">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="relative">
                        <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-pink-500 rounded-2xl flex items-center justify-center shadow-lg shadow-purple-500/50 animate-pulse">
                          <Radio className="w-8 h-8 text-white" />
                        </div>
                        {/* Live Indicator */}
                        <div className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 rounded-full animate-ping"></div>
                        <div className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 rounded-full"></div>
                      </div>
                      <div>
                        <h3 className="text-xl font-bold text-white flex items-center gap-2">
                          🎵 Live Radio
                          <span className="px-3 py-1 bg-red-500/20 border border-red-500/30 rounded-full text-xs font-bold text-red-400 uppercase tracking-wider animate-pulse">
                            On Air
                          </span>
                        </h3>
                        <p className="text-sm text-purple-300">WVOD Power 93 - The Voice of the Diaspora</p>
                      </div>
                    </div>
                    
                    {/* Equalizer Animation */}
                    <div className="hidden md:flex items-end gap-1 h-10">
                      {[1, 2, 3, 4, 5].map((bar, i) => (
                        <div
                          key={i}
                          className="w-2 bg-gradient-to-t from-purple-500 to-pink-500 rounded-full animate-bounce"
                          style={{
                            height: `${20 + Math.random() * 80}%`,
                            animationDelay: `${i * 0.1}s`,
                            animationDuration: `${0.5 + Math.random() * 0.5}s`,
                          }}
                        ></div>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Radio.co Player Iframe */}
                <div className="relative">
                  <iframe 
                    src="https://s4.radio.co/s5f6084ed4/listen" 
                    frameBorder="0" 
                    scrolling="no" 
                    height="200" 
                    width="100%"
                    title="WVOD Power 93 Live Stream"
                    className="w-full"
                  ></iframe>
                </div>

                {/* Decorative Bottom Bar */}
                <div className="h-1 bg-gradient-to-r from-purple-500 via-pink-500 to-purple-500"></div>
              </Card>
            </div>

            {/* Listen Now CTA */}
            <div className="text-center mt-8">
              <button className="group relative px-8 py-4 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full font-bold text-white uppercase tracking-wider shadow-lg shadow-purple-500/50 hover:shadow-xl hover:shadow-purple-500/70 transition-all duration-300 transform hover:scale-105">
                <span className="flex items-center gap-3">
                  <Play className="w-5 h-5 group-hover:animate-pulse" />
                  Listen Live Now
                  <Volume2 className="w-5 h-5 group-hover:animate-pulse" />
                </span>
              </button>
            </div>
          </div>

          {/* Promo Video */}
          <div className="max-w-4xl mx-auto animate-fade-in-up delay-600">
            <Card className="bg-black/50 backdrop-blur-sm border border-white/10 rounded-3xl overflow-hidden shadow-2xl relative">
              <video 
                ref={promoVideoRef}
                autoPlay
                muted
                loop
                playsInline
                className="w-full aspect-video object-contain bg-black"
              >
                <source src="https://video.wixstatic.com/video/4bfe2c_db0d778e179a423eb2a97bb732047c6d/1080p/mp4/file.mp4" type="video/mp4" />
              </video>
              {/* Custom Unmute Button */}
              <button
                onClick={togglePromoMute}
                className="absolute bottom-4 right-4 p-3 bg-black/60 backdrop-blur-sm border border-white/20 rounded-full hover:bg-purple-500/60 hover:border-purple-400 transition-all duration-300 group"
                aria-label={promoVideoMuted ? "Unmute video" : "Mute video"}
              >
                {promoVideoMuted ? (
                  <VolumeX className="w-5 h-5 text-white/70 group-hover:text-white" />
                ) : (
                  <Volume2 className="w-5 h-5 text-purple-400 group-hover:text-white animate-pulse" />
                )}
              </button>
            </Card>
          </div>
        </div>
      </section>

      {/* Broadcast Schedule Section */}
      <section className="py-24 px-4 bg-black/30 backdrop-blur-sm">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-5xl font-black mb-4 animate-fade-in-up">Broadcast Schedule</h2>
            <p className="text-xl text-gray-400 animate-fade-in-up delay-200">See what's playing now and coming up next</p>
          </div>

          {/* Now Playing Widget - Song Display Only */}
          <Card className="bg-gradient-to-br from-purple-500/20 to-pink-500/20 backdrop-blur-lg border border-purple-500/30 rounded-3xl p-6 shadow-2xl mb-8">
            <div className="flex items-center justify-center gap-4">
              <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse"></div>
              <h3 className="text-xl font-bold text-white">Now Playing</h3>
            </div>
            <NowPlayingTrack />
          </Card>

          {/* Featured Shows */}
          <Card className="bg-white/10 backdrop-blur-lg border border-white/20 rounded-3xl p-8 shadow-2xl">
            <h3 className="text-2xl font-bold mb-6 text-center text-white">Featured Shows</h3>
            <div className="grid md:grid-cols-3 gap-4">
              {featuredShows.map((item, i) => (
                <div key={item.id || i} className={`p-6 rounded-2xl border transition-all duration-300 hover:scale-105 ${
                  item.status === 'On Air' 
                    ? 'bg-gradient-to-br from-purple-500/30 to-pink-500/30 border-purple-400/50' 
                    : item.status === 'Up Next'
                    ? 'bg-white/10 border-white/30'
                    : 'bg-white/5 border-white/20'
                }`}>
                  <span className={`inline-block px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider mb-3 ${
                    item.status === 'On Air' 
                      ? 'bg-purple-500 text-white' 
                      : item.status === 'Up Next'
                      ? 'bg-pink-500/80 text-white'
                      : 'bg-gray-500/50 text-gray-200'
                  }`}>
                    {item.status === 'On Air' && <span className="inline-block w-2 h-2 bg-white rounded-full mr-2 animate-pulse"></span>}
                    {item.status}
                  </span>
                  <h4 className="text-lg font-bold mb-2 text-white">{item.show}</h4>
                  <p className="text-sm text-purple-300 font-mono mb-2">{item.time}</p>
                  <p className="text-sm text-gray-300">{item.desc}</p>
                </div>
              ))}
            </div>
            <div className="text-center mt-8">
              <a 
                href="https://www.wvod93.com/" 
                target="_blank" 
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-purple-400 hover:text-pink-400 transition-colors"
              >
                <Calendar className="w-4 h-4" />
                View Full Schedule on WVOD93.com
              </a>
            </div>
          </Card>
        </div>
      </section>

      {/* Throwback of the Week */}
      <section className="py-24 px-4">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-5xl font-black mb-4">Throwback of the Week</h2>
            <p className="text-xl text-gray-400">Relive the classics that defined an era</p>
          </div>

          <Card className="bg-gradient-to-br from-purple-500/10 via-pink-500/10 to-purple-500/10 backdrop-blur-lg border border-white/10 rounded-3xl p-8 shadow-2xl">
            <div className="flex items-center gap-6 mb-6">
              <div className="w-32 h-32 rounded-2xl bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center shadow-lg shadow-purple-500/50">
                <Music className="w-16 h-16 text-white" />
              </div>
              <div>
                <h3 className="text-2xl font-bold mb-2">You Remind Me</h3>
                <p className="text-gray-400">Featured Classic Track</p>
              </div>
            </div>

            <div className="bg-black/30 backdrop-blur-sm rounded-2xl p-6 border border-white/10 relative">
              <audio 
                ref={audioRef}
                muted
                loop
                className="w-full"
              >
                <source src="https://customer-assets.emergentagent.com/job_2185839d-9032-40c2-932e-8d0d429a12de/artifacts/ko47pubq_04%20YOU%20REMIND%20ME.mp3" type="audio/mpeg" />
                Your browser does not support the audio element.
              </audio>
              
              {/* Custom Play/Unmute Button */}
              <div className="flex items-center justify-center mt-4">
                <button
                  onClick={toggleAudioMute}
                  className={`flex items-center gap-3 px-6 py-3 rounded-full font-bold transition-all duration-300 transform hover:scale-105 ${
                    audioMuted 
                      ? 'bg-gradient-to-r from-purple-500 to-pink-500 text-white hover:shadow-lg hover:shadow-purple-500/50' 
                      : 'bg-green-500 text-white hover:shadow-lg hover:shadow-green-500/50 animate-pulse'
                  }`}
                  aria-label={audioMuted ? "Play track" : "Pause track"}
                >
                  {audioMuted ? (
                    <>
                      <Play className="w-5 h-5" />
                      Play Track
                    </>
                  ) : (
                    <>
                      <Volume2 className="w-5 h-5" />
                      Now Playing
                    </>
                  )}
                </button>
              </div>
            </div>
          </Card>
        </div>
      </section>

      {/* Featured Video Section */}
      <section className="py-24 px-4 bg-gradient-to-b from-gray-900 to-black">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-5xl font-black mb-4">Featured Video</h2>
            <p className="text-xl text-gray-400">Watch the latest from WVOD Power 93</p>
          </div>

          <Card className="bg-white/5 backdrop-blur-lg border border-white/10 rounded-3xl overflow-hidden shadow-2xl hover:border-purple-500/50 transition-all duration-300">
            <div className="aspect-video bg-black relative group">
              <video 
                controls 
                loop
                autoPlay
                muted
                playsInline
                className="w-full h-full object-contain"
                style={{ objectFit: 'contain' }}
                poster=""
              >
                <source src="https://video.wixstatic.com/video/4bfe2c_1c329b59a6924266bc872fd24025717d/1080p/mp4/file.mp4" type="video/mp4" />
                Your browser does not support the video tag.
              </video>
            </div>

            {/* Video Description */}
            <div className="p-6 bg-white/5">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center flex-shrink-0">
                  <Radio className="w-6 h-6 text-white" />
                </div>
                <div className="flex-1">
                  <h4 className="text-lg font-bold text-white mb-2">Featured Content from WVOD Power 93</h4>
                  <p className="text-sm text-gray-400 mb-3">
                    Experience WVOD Power 93 - The Voice of the Diaspora. Your destination for the best music, entertainment, and community connection.
                  </p>
                  <div className="flex items-center gap-4 text-xs text-white/60">
                    <span>🎵 Looping</span>
                    <span>•</span>
                    <span>Featured Video</span>
                    <span>•</span>
                    <span className="text-purple-400">#WVODPower93</span>
                  </div>
                </div>
              </div>
            </div>
          </Card>
        </div>
      </section>

      {/* Music Powered By with Large Logo */}
      <section id="about" className="py-24 px-4 bg-black/30 backdrop-blur-sm">
        <div className="max-w-6xl mx-auto">
          <Card className="bg-gradient-to-br from-purple-500/10 to-pink-500/10 backdrop-blur-lg border border-white/10 rounded-3xl p-12 shadow-2xl">
            <div className="flex flex-col lg:flex-row items-center gap-12">
              <div className="lg:w-1/2 flex justify-center">
                <img 
                  src="https://static.wixstatic.com/media/4bfe2c_5a792627a08d495cb304086fc0206377~mv2.png" 
                  alt="WVOD Power 93 Logo" 
                  className="w-full max-w-lg hover:scale-105 transition-transform duration-500"
                />
              </div>
              <div className="lg:w-1/2">
                <h2 className="text-4xl lg:text-5xl font-black mb-6">
                  <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-pink-400">
                    MUSIC POWERED BY
                  </span><br />
                  <span className="text-transparent bg-clip-text bg-gradient-to-r from-gray-900 via-gray-700 to-black">
                    INFAMOUS SOLJAH
                  </span>
                  <span className="text-white"> Sound</span>
                </h2>
                <p className="text-lg text-gray-300 mb-6 leading-relaxed">
                  Join <span className="text-purple-400 font-bold">DJ MG</span>, <span className="text-purple-400 font-bold">C Fresh</span>, <span className="text-purple-400 font-bold">Infamous SoLJah sound</span> and the rest of the WVOD POWER 93 team as we take you on a musical journey through the ages.
                </p>
                <p className="text-lg text-gray-300 leading-relaxed">
                  From the golden era to modern hits, we curate the perfect blend of tracks that celebrate the rich tapestry of music while providing top of the hour news, informative shows on the history of the diaspora, entertainment and daily devotions.
                </p>
                <div className="flex flex-wrap gap-2 mt-6">
                  {['Hip Hop', 'House', 'RnB', 'Reggae', 'Dancehall', 'Calypso', 'Soca', 'Reggaeton', 'Afrobeats'].map((genre, i) => (
                    <span key={i} className="px-4 py-2 bg-purple-500/20 border border-purple-500/30 rounded-full text-sm font-bold text-purple-400 hover:bg-purple-500/30 transition-all duration-300 cursor-pointer">
                      {genre}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </Card>
        </div>
      </section>

      {/* Weekly Shows */}
      <section id="schedule" className="py-24 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-5xl font-black mb-4">Weekly Shows</h2>
            <p className="text-xl text-gray-400">Tune in to your favorite programs</p>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {[
              { name: 'Morning Drive', time: 'Mon-Fri | 6AM-10AM', desc: 'Start your day with classic hits and daily devotions' },
              { name: 'Midday Classics', time: 'Mon-Fri | 12PM-2PM', desc: 'Perfect lunch companion with timeless tracks' },
              { name: 'Diaspora History Hour', time: 'Tue & Thu | 7PM-8PM', desc: 'Informative shows on the history of the diaspora' },
              { name: 'Weekend Vibes', time: 'Sat-Sun | 2PM-6PM', desc: 'Non-stop hits across all genres' },
              { name: 'Reggae Sunday', time: 'Sunday | 9AM-12PM', desc: 'Best in Reggae and Dancehall' },
              { name: 'Late Night Mix', time: 'Fri-Sat | 10PM-2AM', desc: 'Late night energy with Afrobeats & House' },
            ].map((show, i) => (
              <Card key={i} className="bg-white/5 backdrop-blur-lg border border-white/10 rounded-2xl p-6 hover:border-purple-500/50 hover:shadow-lg hover:shadow-purple-500/20 transition-all duration-300 group cursor-pointer">
                <div className="flex items-start gap-4">
                  <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform duration-300">
                    <Calendar className="w-8 h-8 text-white" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-bold mb-2 group-hover:text-purple-400 transition-colors">{show.name}</h3>
                    <p className="text-purple-400 font-mono text-sm mb-2">{show.time}</p>
                    <p className="text-gray-400">{show.desc}</p>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Event Calendar */}
      <section id="events" className="py-24 px-4 bg-black/30 backdrop-blur-sm">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-5xl font-black mb-4">Upcoming Events</h2>
            <p className="text-xl text-gray-400">Join us for special shows and broadcasts</p>
          </div>

          <div className="grid lg:grid-cols-3 gap-6">
            {[
              { date: '15', month: 'JAN', name: 'Live DJ Set: C Fresh', time: 'Friday 8PM-11PM', type: 'Live' },
              { date: '22', month: 'JAN', name: 'Diaspora Stories Hour', time: 'Tuesday 7PM-8PM', type: 'Special' },
              { date: '28', month: 'JAN', name: 'Reggae Sunday Special', time: 'Sunday 9AM-12PM', type: 'Featured' },
            ].map((event, i) => (
              <Card key={i} className="bg-white/5 backdrop-blur-lg border border-white/10 rounded-2xl p-6 hover:border-purple-500/50 hover:shadow-lg hover:shadow-purple-500/20 transition-all duration-300 hover:scale-105 group cursor-pointer">
                <div className="flex items-start gap-4 mb-4">
                  <div className="bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl p-4 text-center min-w-[80px] shadow-lg">
                    <div className="text-3xl font-black text-white">{event.date}</div>
                    <div className="text-xs font-bold text-white/80 uppercase tracking-wider">{event.month}</div>
                  </div>
                  <div className="flex-1">
                    <span className="inline-block px-3 py-1 bg-purple-500/20 border border-purple-500/30 rounded-full text-xs font-bold text-purple-400 uppercase tracking-wider mb-2">
                      {event.type}
                    </span>
                    <h3 className="text-xl font-bold mb-1 group-hover:text-purple-400 transition-colors">{event.name}</h3>
                    <p className="text-sm text-gray-400 font-mono">{event.time}</p>
                  </div>
                </div>
                <button className="w-full py-3 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full font-bold uppercase text-sm tracking-wider hover:shadow-lg hover:shadow-purple-500/50 transition-all duration-300">
                  Add to Calendar
                </button>
              </Card>
            ))}
          </div>

          {/* Calendar Widget - Wix Events */}
          <Card className="mt-12 bg-white/5 backdrop-blur-lg border border-white/10 rounded-3xl p-6 overflow-hidden">
            <h3 className="text-2xl font-bold mb-4 text-center">Full Event Calendar</h3>
            <div className="grid grid-cols-7 gap-1 mb-6">
              {['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'].map((day) => (
                <div key={day} className="text-center text-xs font-bold text-purple-400 py-2">{day}</div>
              ))}
              {Array.from({length: 31}, (_, i) => i + 1).map((date) => (
                <div 
                  key={date} 
                  className={`text-center py-2 rounded-lg text-sm cursor-pointer transition-all duration-200 
                    ${[15, 22, 28].includes(date) 
                      ? 'bg-gradient-to-br from-purple-500 to-pink-500 text-white font-bold' 
                      : 'bg-white/5 hover:bg-white/10 text-gray-400'}`}
                >
                  {date}
                </div>
              ))}
            </div>
            <div className="text-center">
              <a 
                href="https://www.wvod93.com/" 
                target="_blank" 
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full text-white font-bold hover:shadow-lg hover:shadow-purple-500/50 transition-all duration-300"
              >
                <Calendar className="w-4 h-4" />
                View Full Calendar on WVOD93.com
              </a>
            </div>
          </Card>
        </div>
      </section>

      {/* News Section */}
      <section className="py-24 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-5xl font-black mb-4">News - Sports - Entertainment</h2>
            <p className="text-xl text-gray-400 mb-8">Stay updated with Caribbean and diaspora news</p>
          </div>

          {/* Scrolling News Ticker */}
          <a href="https://www.wvod93.com/" target="_blank" rel="noopener noreferrer" className="block mb-12 overflow-hidden bg-gradient-to-r from-purple-500/20 via-pink-500/20 to-purple-500/20 backdrop-blur-lg border border-white/10 rounded-2xl hover:border-purple-500/50 transition-all duration-300">
            <div className="flex items-center">
              {/* Breaking News Label */}
              <div className="bg-gradient-to-r from-purple-500 to-pink-500 px-6 py-4 flex-shrink-0">
                <span className="font-bold text-white uppercase tracking-wider text-sm flex items-center gap-2">
                  <span className="w-2 h-2 bg-white rounded-full animate-pulse"></span>
                  Breaking News
                </span>
              </div>
              
              {/* Scrolling News Content */}
              <div className="flex-1 overflow-hidden py-4">
                <div className="animate-scroll whitespace-nowrap">
                  <span className="inline-block px-8 text-white font-medium">
                    🌀 Hurricane Melissa death toll rises to 67 as insured losses in Jamaica could top $4 billion
                  </span>
                  <span className="inline-block px-8 text-purple-400">•</span>
                  <span className="inline-block px-8 text-white font-medium">
                    🎤 Vybz Kartel in Toronto: The Jamaican dancehall superstar talks life after prison and his relationship with Drake
                  </span>
                  <span className="inline-block px-8 text-purple-400">•</span>
                  <span className="inline-block px-8 text-white font-medium">
                    🏆 Bad Bunny, Shenseea & Vybz Kartel Triumph at 2025 Caribbean Music Awards
                  </span>
                  <span className="inline-block px-8 text-purple-400">•</span>
                  <span className="inline-block px-8 text-white font-medium">
                    🎵 Bounty Killer receives Lifetime Achievement Award at Caribbean Music Awards
                  </span>
                  <span className="inline-block px-8 text-purple-400">•</span>
                  <span className="inline-block px-8 text-white font-medium">
                    📻 WVOD Power 93 - The Voice of the Diaspora bringing music the way it used to be
                  </span>
                  <span className="inline-block px-8 text-purple-400">•</span>
                  {/* Duplicate for seamless loop */}
                  <span className="inline-block px-8 text-white font-medium">
                    🌀 Hurricane Melissa death toll rises to 67 as insured losses in Jamaica could top $4 billion
                  </span>
                  <span className="inline-block px-8 text-purple-400">•</span>
                  <span className="inline-block px-8 text-white font-medium">
                    🎤 Vybz Kartel in Toronto: The Jamaican dancehall superstar talks life after prison and his relationship with Drake
                  </span>
                  <span className="inline-block px-8 text-purple-400">•</span>
                  <span className="inline-block px-8 text-white font-medium">
                    🏆 Bad Bunny, Shenseea & Vybz Kartel Triumph at 2025 Caribbean Music Awards
                  </span>
                  <span className="inline-block px-8 text-purple-400">•</span>
                  <span className="inline-block px-8 text-white font-medium">
                    🎵 Bounty Killer receives Lifetime Achievement Award at Caribbean Music Awards
                  </span>
                  <span className="inline-block px-8 text-purple-400">•</span>
                  <span className="inline-block px-8 text-white font-medium">
                    📻 WVOD Power 93 - The Voice of the Diaspora bringing music the way it used to be
                  </span>
                </div>
              </div>
            </div>
          </a>

          <Card className="bg-white/5 backdrop-blur-lg border border-white/10 rounded-3xl p-8">
            <div className="text-center text-gray-400 mb-6">
              <p className="text-sm">News powered by ShareiiiT</p>
            </div>
            
            <div className="grid md:grid-cols-2 gap-6">
              <a href="https://www.wvod93.com/" target="_blank" rel="noopener noreferrer" className="border-b border-white/10 pb-6 last:border-0 group">
                <div className="w-full h-48 bg-gradient-to-br from-purple-500/20 to-pink-500/20 rounded-2xl mb-4 overflow-hidden">
                  <img src="https://static.wixstatic.com/media/4bfe2c_9667b8ff7542457db2158905420eb1cd~mv2.webp" alt="Hurricane Melissa" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                </div>
                <h3 className="text-xl font-bold mb-2 group-hover:text-purple-400 transition-colors cursor-pointer">Hurricane Melissa death toll rises to 67</h3>
                <p className="text-sm text-gray-400 mb-2">November 10, 2025</p>
                <p className="text-gray-300">Insured losses in Jamaica could top $4 billion as authorities assess damage...</p>
              </a>
              <a href="https://www.wvod93.com/" target="_blank" rel="noopener noreferrer" className="border-b border-white/10 pb-6 last:border-0 group">
                <div className="w-full h-48 bg-gradient-to-br from-purple-500/20 to-pink-500/20 rounded-2xl mb-4 overflow-hidden">
                  <img src="https://static.wixstatic.com/media/4bfe2c_9f721970235f4035b2235325228763ec~mv2.webp" alt="Vybz Kartel" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                </div>
                <h3 className="text-xl font-bold mb-2 group-hover:text-purple-400 transition-colors cursor-pointer">Vybz Kartel in Toronto</h3>
                <p className="text-sm text-gray-400 mb-2">November 6, 2025</p>
                <p className="text-gray-300">The Jamaican dancehall superstar talks life after prison and his relationship with Drake...</p>
              </a>
            </div>
            
            <div className="text-center mt-8">
              <a 
                href="https://www.wvod93.com/" 
                target="_blank" 
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full text-white font-bold hover:shadow-lg hover:shadow-purple-500/50 transition-all duration-300"
              >
                Read More News on WVOD93.com
                <span>→</span>
              </a>
            </div>
          </Card>
        </div>
      </section>

      {/* Culture Section */}
      <section className="py-24 px-4 bg-black/30 backdrop-blur-sm">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <p className="text-3xl font-black text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-pink-400 mb-4 animate-flash">Lets Talk</p>
            <h2 className="text-5xl font-black mb-4">Culture</h2>
            <p className="text-xl text-gray-400">Experience vibrant culture through music videos</p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            <Card className="bg-white/5 backdrop-blur-lg border border-white/10 rounded-3xl overflow-hidden hover:border-purple-500/50 hover:shadow-lg hover:shadow-purple-500/20 transition-all duration-300">
              <div className="aspect-video">
                <iframe 
                  width="100%" 
                  height="100%" 
                  src="https://www.youtube.com/embed/Xc9YqzC-hEs" 
                  title="Baby Cham"
                  frameBorder="0" 
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" 
                  allowFullScreen
                ></iframe>
              </div>
              <div className="p-6">
                <h3 className="text-lg font-bold mb-2">Baby Cham - Hustling In My Blood</h3>
                <p className="text-sm text-gray-400">Featuring Stephen & Damian Marley</p>
              </div>
            </Card>

            <Card className="bg-white/5 backdrop-blur-lg border border-white/10 rounded-3xl overflow-hidden hover:border-purple-500/50 hover:shadow-lg hover:shadow-purple-500/20 transition-all duration-300">
              <div className="p-6 pb-0">
                <h3 className="text-2xl font-black text-transparent bg-clip-text bg-gradient-to-r from-purple-400 via-pink-400 to-yellow-400 uppercase tracking-wide animate-pulse">Whats on?</h3>
              </div>
              <div className="aspect-video p-4 pt-2">
                <iframe 
                  width="100%" 
                  height="100%" 
                  src="https://www.youtube.com/embed/1Z2k7HmREGw" 
                  title="Featured Video 2"
                  frameBorder="0" 
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" 
                  allowFullScreen
                  className="rounded-xl"
                ></iframe>
              </div>
              <div className="px-6 pb-6">
                <p className="text-sm text-gray-400">Top TV Shows Premiering</p>
              </div>
            </Card>
          </div>
        </div>
      </section>

      {/* Blog Preview Section */}
      <section className="py-24 px-4 bg-black/30 backdrop-blur-sm">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-5xl font-black mb-4">
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-pink-400">The VOD Squad Blog</span>
            </h2>
            <p className="text-xl text-gray-400">So Boom! Let's Talk about it - Connecting the Dots</p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 mb-8">
            {/* Blog Post 1 */}
            <a 
              href="https://www.wvod93.com/post/king-phillips-war" 
              target="_blank" 
              rel="noopener noreferrer"
              className="group"
            >
              <Card className="bg-white/5 backdrop-blur-lg border border-white/10 rounded-3xl overflow-hidden hover:border-purple-500/50 hover:shadow-lg hover:shadow-purple-500/20 transition-all duration-300 h-full">
                <div className="aspect-video bg-gradient-to-br from-purple-500/20 to-pink-500/20 overflow-hidden">
                  <img 
                    src="https://static.wixstatic.com/media/4bfe2c_f7a6df6bb157424281750ec498c250ea~mv2.png" 
                    alt="King Phillips War"
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="p-6">
                  <span className="inline-block px-3 py-1 bg-purple-500/20 border border-purple-500/30 rounded-full text-xs font-bold text-purple-400 uppercase tracking-wider mb-3">History</span>
                  <h3 className="text-xl font-bold mb-2 group-hover:text-purple-400 transition-colors">King Phillips War</h3>
                  <p className="text-sm text-gray-400 mb-3">Who is King Phillip? And who was he at War with? Metacomet was chief of the Wampanoag Confederacy...</p>
                  <p className="text-xs text-gray-500">Nov 19, 2025 • 2 min read</p>
                </div>
              </Card>
            </a>

            {/* Blog Post 2 */}
            <a 
              href="https://www.wvod93.com/post/who-are-the-moors" 
              target="_blank" 
              rel="noopener noreferrer"
              className="group"
            >
              <Card className="bg-white/5 backdrop-blur-lg border border-white/10 rounded-3xl overflow-hidden hover:border-purple-500/50 hover:shadow-lg hover:shadow-purple-500/20 transition-all duration-300 h-full">
                <div className="aspect-video bg-gradient-to-br from-purple-500/20 to-pink-500/20 overflow-hidden">
                  <img 
                    src="https://static.wixstatic.com/media/4bfe2c_cd42fd287b3f4b3da445c3148a31cf08~mv2.png" 
                    alt="Who are The Moors?"
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="p-6">
                  <span className="inline-block px-3 py-1 bg-purple-500/20 border border-purple-500/30 rounded-full text-xs font-bold text-purple-400 uppercase tracking-wider mb-3">History</span>
                  <h3 className="text-xl font-bold mb-2 group-hover:text-purple-400 transition-colors">Who are The Moors?</h3>
                  <p className="text-sm text-gray-400 mb-3">Moors are considered to have advanced in mathematics, astronomy, architecture, cuisine, medicine...</p>
                  <p className="text-xs text-gray-500">Nov 19, 2025 • 1 min read</p>
                </div>
              </Card>
            </a>
          </div>

          {/* View All Posts Button */}
          <div className="text-center">
            <a 
              href="https://www.wvod93.com/the-pop-blog" 
              target="_blank" 
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full text-white font-bold hover:shadow-lg hover:shadow-purple-500/50 transition-all duration-300 transform hover:scale-105"
            >
              View All Posts
              <span className="text-xl">→</span>
            </a>
          </div>
        </div>
      </section>

      {/* Weather Widget */}
      <section className="py-24 px-4">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-5xl font-black mb-4">Weather</h2>
            <p className="text-xl text-gray-400">Current conditions</p>
          </div>

          <Card className="bg-gradient-to-br from-blue-500/10 via-purple-500/10 to-pink-500/10 backdrop-blur-lg border border-white/10 rounded-3xl p-4 overflow-hidden">
            <div className="w-full flex justify-center">
              <iframe 
                src="https://www.meteoblue.com/en/weather/widget/daily?geoloc=detect&days=4&tempunit=CELSIUS&windunit=KILOMETER_PER_HOUR&precipunit=MILLIMETER&coloured=coloured&pictoicon=0&pictoicon=1&maxtemperature=0&maxtemperature=1&mintemperature=0&mintemperature=1&windspeed=0&windgust=0&winddirection=0&uv=0&humidity=0&precipitation=0&precipitationprobability=0&precipitationprobability=1&spot=0&pressure=0&layout=dark"
                frameBorder="0"
                scrolling="NO"
                sandbox="allow-same-origin allow-scripts allow-popups allow-popups-to-escape-sandbox"
                style={{width: '100%', height: '220px'}}
                title="Weather Widget"
              ></iframe>
            </div>
            <p className="text-center text-xs text-gray-500 mt-2">
              Weather data by <a href="https://www.meteoblue.com" target="_blank" rel="noopener noreferrer" className="text-purple-400 hover:underline">meteoblue</a>
            </p>
          </Card>
        </div>
      </section>

      {/* Instagram Feed Section */}
      <section className="py-24 px-4 bg-black/30 backdrop-blur-sm">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-5xl font-black mb-4">
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-400 via-pink-400 to-yellow-400">Instagram</span>
            </h2>
            <p className="text-xl text-gray-400">Follow us @wvodpower93</p>
          </div>

          <Card className="bg-gradient-to-br from-purple-500/10 via-pink-500/10 to-orange-500/10 backdrop-blur-lg border border-white/10 rounded-3xl p-4 overflow-hidden">
            {/* Elfsight Instagram Feed Widget */}
            <div className="elfsight-app-ef6bc122-35e4-4788-9f86-c7abb147a0ed" data-elfsight-app-lazy></div>
            
            {/* Follow Button */}
            <div className="text-center mt-6">
              <a 
                href="https://www.instagram.com/wvodpower93/" 
                target="_blank" 
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-purple-500 via-pink-500 to-orange-500 rounded-full text-white font-bold hover:shadow-lg hover:shadow-pink-500/50 transition-all duration-300 transform hover:scale-105"
              >
                <Instagram className="w-5 h-5" />
                Follow @wvodpower93
              </a>
            </div>
          </Card>
        </div>
      </section>

      {/* Content Attribution Ticker */}
      <section className="py-8 px-4">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl font-black text-center mb-6 text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-pink-400">Content Attribution</h2>
          <div className="overflow-hidden bg-gradient-to-r from-pink-500/20 via-purple-500/20 to-pink-500/20 backdrop-blur-lg border border-white/10 rounded-2xl">
            <div className="flex items-center">
              {/* Credits Label */}
              <div className="bg-gradient-to-r from-pink-500 to-purple-500 px-6 py-4 flex-shrink-0">
                <span className="font-bold text-white uppercase tracking-wider text-sm flex items-center gap-2">
                  <span className="w-2 h-2 bg-white rounded-full animate-pulse"></span>
                  Credits
                </span>
              </div>
              
              {/* Scrolling Credits Content - Dynamic from API */}
              <div className="flex-1 overflow-hidden py-4">
                <div className="animate-scroll whitespace-nowrap">
                  {/* First set */}
                  {attributionCredits.map((credit, i) => (
                    <React.Fragment key={`credit-${i}`}>
                      <span className="inline-block px-6 text-white font-medium">
                        {credit.platform === 'IG' && '📸'} 
                        {credit.platform === 'TikTok' && '🎵'} 
                        {credit.platform === 'YouTube' && '▶️'} 
                        {credit.platform === 'IG' ? 'IG ' : credit.platform === 'TikTok' ? 'TikTok ' : 'YouTube: '}
                        {credit.handle}
                      </span>
                      <span className="inline-block px-4 text-pink-400">•</span>
                    </React.Fragment>
                  ))}
                  {/* Duplicate set for seamless loop */}
                  {attributionCredits.map((credit, i) => (
                    <React.Fragment key={`credit-dup-${i}`}>
                      <span className="inline-block px-6 text-white font-medium">
                        {credit.platform === 'IG' && '📸'} 
                        {credit.platform === 'TikTok' && '🎵'} 
                        {credit.platform === 'YouTube' && '▶️'} 
                        {credit.platform === 'IG' ? 'IG ' : credit.platform === 'TikTok' ? 'TikTok ' : 'YouTube: '}
                        {credit.handle}
                      </span>
                      <span className="inline-block px-4 text-pink-400">•</span>
                    </React.Fragment>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Gratitude Board */}
      <section className="py-24 px-4">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-5xl font-black mb-4">Gratitude Board</h2>
            <p className="text-xl text-gray-400">Share what you're grateful for today</p>
          </div>

          <Card className="bg-gradient-to-br from-purple-500/10 to-pink-500/10 backdrop-blur-lg border border-white/10 rounded-3xl p-8 mb-8">
            <form onSubmit={handleGratitudeSubmit} className="space-y-4">
              <Textarea
                placeholder="I'm grateful for..."
                value={gratitudeForm.message}
                onChange={(e) => setGratitudeForm({...gratitudeForm, message: e.target.value})}
                className="bg-white/5 border-white/10 text-white placeholder:text-gray-500 min-h-32 rounded-xl"
                required
              />
              <Input
                placeholder="Your name (optional)"
                value={gratitudeForm.name}
                onChange={(e) => setGratitudeForm({...gratitudeForm, name: e.target.value})}
                className="bg-white/5 border-white/10 text-white placeholder:text-gray-500 rounded-xl"
              />
              <Button
                type="submit"
                className="w-full bg-gradient-to-r from-purple-500 to-pink-500 hover:shadow-lg hover:shadow-purple-500/50 rounded-full py-6 font-bold uppercase tracking-wider transition-all duration-300"
              >
                <Heart className="w-5 h-5 mr-2" />
                Share Gratitude
              </Button>
            </form>
          </Card>

          <div className="grid md:grid-cols-2 gap-6">
            {gratitudeMessages.slice(0, 4).map((item, i) => (
              <Card key={item.id || i} className="bg-white/5 backdrop-blur-lg border border-white/10 rounded-2xl p-6 hover:border-purple-500/50 transition-all duration-300">
                <p className="text-white mb-3 italic">"{item.message}"</p>
                <p className="text-sm text-purple-400">- {item.name || 'Anonymous'}</p>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-24 px-4 bg-black/30 backdrop-blur-sm">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-5xl font-black mb-4">Tell Us</h2>
            <p className="text-xl text-gray-400">Have feedback or suggestions? We'd love to hear from you!</p>
          </div>

          <div className="grid lg:grid-cols-2 gap-12">
            {/* Contact Info */}
            <div className="space-y-6">
              <Card className="bg-white/5 backdrop-blur-lg border border-white/10 rounded-2xl p-6 hover:border-purple-500/50 transition-all duration-300">
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl flex items-center justify-center">
                    <Mail className="w-7 h-7 text-white" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-400">Email</p>
                    <a href="mailto:1WVODPower93@proton.me" className="text-lg font-bold hover:text-purple-400 transition-colors">1WVODPower93@proton.me</a>
                  </div>
                </div>
              </Card>

              <Card className="bg-white/5 backdrop-blur-lg border border-white/10 rounded-2xl p-6 hover:border-purple-500/50 transition-all duration-300">
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl flex items-center justify-center">
                    <Phone className="w-7 h-7 text-white" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-400">Phone</p>
                    <p className="text-lg font-bold">+1 (555) POWER-93</p>
                  </div>
                </div>
              </Card>

              <Card className="bg-white/5 backdrop-blur-lg border border-white/10 rounded-2xl p-6 hover:border-purple-500/50 transition-all duration-300">
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl flex items-center justify-center">
                    <MapPin className="w-7 h-7 text-white" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-400">Location</p>
                    <p className="text-lg font-bold">Broadcasting Worldwide</p>
                  </div>
                </div>
              </Card>

              <div className="flex gap-4 pt-4">
                <a href="#" className="w-14 h-14 bg-purple-500/20 hover:bg-gradient-to-br hover:from-purple-500 hover:to-pink-500 rounded-xl flex items-center justify-center transition-all duration-300 border border-purple-500/30">
                  <Facebook className="w-6 h-6" />
                </a>
                <a href="#" className="w-14 h-14 bg-purple-500/20 hover:bg-gradient-to-br hover:from-purple-500 hover:to-pink-500 rounded-xl flex items-center justify-center transition-all duration-300 border border-purple-500/30">
                  <Instagram className="w-6 h-6" />
                </a>
                <a href="#" className="w-14 h-14 bg-purple-500/20 hover:bg-gradient-to-br hover:from-purple-500 hover:to-pink-500 rounded-xl flex items-center justify-center transition-all duration-300 border border-purple-500/30">
                  <Twitter className="w-6 h-6" />
                </a>
              </div>
            </div>

            {/* Contact Form */}
            <Card className="bg-white/5 backdrop-blur-lg border border-white/10 rounded-3xl p-8">
              <h3 className="text-2xl font-bold mb-6">Send us a message</h3>
              <form onSubmit={handleContactSubmit} className="space-y-4">
                <Input
                  type="text"
                  placeholder="Your Name"
                  value={feedbackForm.name}
                  onChange={(e) => setFeedbackForm({ ...feedbackForm, name: e.target.value })}
                  className="bg-white/5 border-white/10 text-white placeholder:text-gray-500 rounded-xl"
                  required
                />
                <Input
                  type="email"
                  placeholder="Your Email"
                  value={feedbackForm.email}
                  onChange={(e) => setFeedbackForm({ ...feedbackForm, email: e.target.value })}
                  className="bg-white/5 border-white/10 text-white placeholder:text-gray-500 rounded-xl"
                  required
                />
                <Textarea
                  placeholder="Your Message or Feedback"
                  value={feedbackForm.message}
                  onChange={(e) => setFeedbackForm({ ...feedbackForm, message: e.target.value })}
                  className="bg-white/5 border-white/10 text-white placeholder:text-gray-500 min-h-32 rounded-xl"
                  required
                />
                <Button
                  type="submit"
                  className="w-full bg-gradient-to-r from-purple-500 to-pink-500 hover:shadow-lg hover:shadow-purple-500/50 rounded-full py-6 font-bold uppercase tracking-wider transition-all duration-300"
                >
                  <Send className="w-5 h-5 mr-2" />
                  Send Message
                </Button>
              </form>
            </Card>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-4 bg-black/50 backdrop-blur-lg border-t border-white/10">
        <div className="max-w-6xl mx-auto text-center">
          <div className="flex items-center justify-center gap-3 mb-4">
            <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl flex items-center justify-center">
              <Radio className="w-6 h-6 text-white" />
            </div>
            <h3 className="text-2xl font-black">WVOD POWER 93</h3>
          </div>
          <p className="text-gray-400 mb-2">The Voice of the Diaspora</p>
          <a href="mailto:1WVODPower93@proton.me" className="text-purple-400 hover:text-pink-400 transition-colors mb-4 block">1WVODPower93@proton.me</a>
          <p className="text-sm text-gray-500">
            © 2025 WVOD Power 93. All rights reserved. | Music the way it used to be.
          </p>
        </div>
      </footer>

      <style jsx>{`
        @keyframes fade-in {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes fade-in-up {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes bounce-slow {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-10px); }
        }
        @keyframes gradient {
          0% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
          100% { background-position: 0% 50%; }
        }
        @keyframes scroll {
          0% { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
        .animate-fade-in { animation: fade-in 1s ease-out; }
        .animate-fade-in-up { animation: fade-in-up 1s ease-out; }
        .delay-200 { animation-delay: 0.2s; }
        .delay-400 { animation-delay: 0.4s; }
        .delay-600 { animation-delay: 0.6s; }
        .delay-1000 { animation-delay: 1s; }
        .animate-bounce-slow { animation: bounce-slow 3s ease-in-out infinite; }
        .animate-gradient { 
          background-size: 200% 200%;
          animation: gradient 3s ease infinite;
        }
        .animate-scroll {
          animation: scroll 30s linear infinite;
        }
        .animate-scroll:hover {
          animation-play-state: paused;
        }
        @keyframes flash {
          0%, 100% { opacity: 1; }
          50% { opacity: 0; }
        }
        .animate-flash {
          animation: flash 1.5s ease-in-out infinite;
        }
      `}</style>
    </div>
  );
};

export default Home3;